import { z } from 'zod';

/** Contratos de Contas (M2). */

export const accountTypeSchema = z.enum([
  'checking', // Conta corrente
  'cash', // Dinheiro
  'savings', // Poupança
  'investment', // Investimentos
  'voucher', // VR/VA
  'other', // Outros
]);
export type AccountType = z.infer<typeof accountTypeSchema>;

/** Rótulos pt-BR por tipo (usados na UI e no agrupamento). */
export const ACCOUNT_TYPE_LABELS: Record<AccountType, string> = {
  checking: 'Conta corrente',
  cash: 'Dinheiro',
  savings: 'Poupança',
  investment: 'Investimentos',
  voucher: 'VR/VA',
  other: 'Outros',
};

/** Ordem de exibição dos grupos por tipo na tela de Contas. */
export const ACCOUNT_TYPE_ORDER: AccountType[] = [
  'checking',
  'cash',
  'savings',
  'investment',
  'voucher',
  'other',
];

const hexColorSchema = z
  .string()
  .regex(/^#[0-9a-fA-F]{6}$/, 'Cor deve ser um hex #RRGGBB.');

export const accountSchema = z.object({
  id: z.string(),
  name: z.string(),
  type: accountTypeSchema,
  color: hexColorSchema,
  icon: z.string().nullable(),
  initialBalanceCents: z.number().int(),
  includeInTotal: z.boolean(),
  archived: z.boolean(),
  sortOrder: z.number().int(),
  /** Saldo inicial + Σ efetivadas da conta (qualquer data). */
  currentBalanceCents: z.number().int(),
  /** Saldo atual + Σ pendentes do mês consultado. */
  predictedBalanceCents: z.number().int(),
});
export type Account = z.infer<typeof accountSchema>;

export const accountsResponseSchema = z.object({
  accounts: z.array(accountSchema),
  /** Totais só das contas com includeInTotal = true e não arquivadas. */
  totalCurrentCents: z.number().int(),
  totalPredictedCents: z.number().int(),
});
export type AccountsResponse = z.infer<typeof accountsResponseSchema>;

export const createAccountBodySchema = z.object({
  name: z.string().trim().min(1, 'Informe uma descrição.').max(60),
  type: accountTypeSchema,
  color: hexColorSchema,
  // Nome lucide / 'brand:<slug>' / '' (nenhum) / data URL de logo enviado (≤~24KB).
  icon: z.string().max(30000).optional(),
  initialBalanceCents: z.number().int().default(0),
  includeInTotal: z.boolean().default(true),
});
export type CreateAccountBody = z.infer<typeof createAccountBodySchema>;

export const updateAccountBodySchema = createAccountBodySchema.partial();
export type UpdateAccountBody = z.infer<typeof updateAccountBodySchema>;

export const adjustBalanceBodySchema = z.object({
  targetBalanceCents: z.number().int(),
  /** Data do ajuste; default = hoje (SP) no service. */
  date: z.string().regex(/^\d{4}-\d{2}-\d{2}$/).optional(),
});
export type AdjustBalanceBody = z.infer<typeof adjustBalanceBodySchema>;
